import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Search, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface UserFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedRole: string;
  onRoleChange: (value: string) => void;
  showActiveOnly: boolean;
  onActiveOnlyChange: (value: boolean) => void;
  showInactiveOnly: boolean;
  onInactiveOnlyChange: (value: boolean) => void;
}

const UserFilters: React.FC<UserFiltersProps> = ({
  searchTerm,
  onSearchChange,
  selectedRole,
  onRoleChange,
  showActiveOnly,
  onActiveOnlyChange,
  showInactiveOnly,
  onInactiveOnlyChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  return (
    <Card className="mb-6">
      <CardHeader className={isMobile ? 'pb-3' : ''}>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Filter className="h-5 w-5" />
          {t('users.filters')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
          
          {/* Search Field */}
          <div className="space-y-2">
            <Label htmlFor="user-search">{t('users.searchByNameEmail')}</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="user-search"
                placeholder={t('users.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Role Filter */}
          <div className="space-y-2">
            <Label>{t('users.filterByRole')}</Label>
            <Select value={selectedRole} onValueChange={onRoleChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('users.allRoles')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('users.allRoles')}</SelectItem>
                <SelectItem value="admin">{t('users.admin')}</SelectItem>
                <SelectItem value="manager">{t('users.manager')}</SelectItem>
                <SelectItem value="staff">{t('users.user')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Users Only */}
          <div className="space-y-2">
            <Label>{t('users.activeUsersOnly')}</Label>
            <div className="flex items-center space-x-2">
              <Switch
                id="active-only"
                checked={showActiveOnly}
                onCheckedChange={onActiveOnlyChange}
              />
              <Label htmlFor="active-only" className="text-sm text-muted-foreground">
                {showActiveOnly ? t('common.yes') : t('common.no')}
              </Label>
            </div>
          </div>

          {/* Inactive Users Only */}
          <div className="space-y-2">
            <Label>{t('users.inactiveUsersOnly')}</Label>
            <div className="flex items-center space-x-2">
              <Switch
                id="inactive-only"
                checked={showInactiveOnly}
                onCheckedChange={onInactiveOnlyChange}
              />
              <Label htmlFor="inactive-only" className="text-sm text-muted-foreground">
                {showInactiveOnly ? t('common.yes') : t('common.no')}
              </Label>
            </div>
          </div>

        </div>
      </CardContent>
    </Card>
  );
};

export default UserFilters;